import { useNavigate } from "react-router-dom";
import {
  LOCAL_STORAGE_KEYS,
  PAGE_RENDER_TYPE,
} from "e3-common/constants/enumerates";
import { toHierarchy, findBtnStyle } from "e3-common/utils/dataUtil";
import {
  removeLocalStorageItem,
  setLocalStorageItem,
  getLocalStorageItem,
  setSessionStorageItem,
  removeSessionStorageItem,
} from "e3-common/utils/localStorageUtil";

import useFetch from "e3-common/hooks/useFetch";
import { useBoundStore } from "e3-common/store/useBoundStore";
import { shallow } from "zustand/shallow";
import { findSelectedMenu } from "e3-common/utils/menuUtil";

export default function useAuth() {
  const navigate = useNavigate();

  const { request } = useFetch();
  setLocalStorageItem(
    LOCAL_STORAGE_KEYS.BUTTON_STYLE,
    JSON.stringify(findBtnStyle(import.meta.env.VITE_BUTTON_STYLE)),
  );

  const { setUserAndMenuState, resetUserAndMenuState } = useBoundStore(
    (state) => ({
      setUserAndMenuState: state.setUserAndMenuState,
      resetUserAndMenuState: state.resetUserAndMenuState,
    }),
    shallow,
  );

  const azureLogin = async () => {
    const response = await request("/azure/login");

    if (response.url) {
      window.location.href = response.url;
    }
  };

  const azureSecure = async (location) => {
    navigate(import.meta.env.VITE_PUBLIC_URL + "/", { replace: true });

    const response = await request("/azure/secure/aad" + location.search);

    if (response) {
      const result = getValues(response);

      const showTopMenuYn = findShowTopMenuCode(result.commonCodes);
      const helpMenuYn = findHelpMenuYn(result.commonCodes);

      setLocalStorageItem(LOCAL_STORAGE_KEYS.AZURE_LOGIN, "Y");
      setSessionStorageItem(
        LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
        result.user.accessToken,
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.COMMON_CODES,
        JSON.stringify(toHierarchy(result.commonCodes, "codeId", "parentId")),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.GRID_PSN,
        JSON.stringify(result.gridPsn),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.SHEET_STYLE,
        JSON.stringify(result.sheetStyle),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.AUTH_LISTS,
        JSON.stringify(result.authLists),
      );

      setUserAndMenuState({
        user: result.user,
        menu: result.menu,
        authBtn: result.authBtn,
        pageRenderType:
          response?.PAGE_RENDER_TYPE?.toUpperCase() || PAGE_RENDER_TYPE.PAGE,
        showTopMenuYn: showTopMenuYn,
        hierarchyMenu: toHierarchy(result.menu, "menuId", "parentId"),
        favoriteMenu: result.favoriteMenu,
        helpMenuYn: helpMenuYn,
        authLists: result.authLists,
      });
    }
  };

  /**
   * ID/PW Login 호출
   */
  const login = async ({ userId, password }) => {
    const requestOptions = {
      body: {
        USER_ID: userId,
        PASSWORD: password,
      },
    };
    const messageOptions = {
      showConfirm: false,
      showAlert: true,
      alertTitle: "로그인 실패",
    };

    const response = await request("/login", requestOptions, messageOptions);

    if (response) {
      const result = getValues(response);

      const showTopMenuYn = findShowTopMenuCode(result.commonCodes);
      const helpMenuYn = findHelpMenuYn(result.commonCodes);

      setSessionStorageItem(
        LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
        result.user.accessToken,
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.COMMON_CODES,
        JSON.stringify(toHierarchy(result.commonCodes, "codeId", "parentId")),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.GRID_PSN,
        JSON.stringify(result.gridPsn),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.SHEET_STYLE,
        JSON.stringify(result.sheetStyle),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.AUTH_LISTS,
        JSON.stringify(result.authLists),
      );

      setUserAndMenuState({
        user: result.user,
        menu: result.menu,
        authBtn: result.authBtn,
        pageRenderType:
          response?.PAGE_RENDER_TYPE?.toUpperCase() || PAGE_RENDER_TYPE.PAGE,
        showTopMenuYn: showTopMenuYn,
        hierarchyMenu: toHierarchy(result.menu, "menuId", "parentId"),
        favoriteMenu: result.favoriteMenu,
        helpMenuYn: helpMenuYn,
        authLists: result.authLists,
      });
    }
  };

  const esskNonUserLogin = async ({ userName, telNo, authNo }) => {
    const requestOptions = {
      body: {
        USER_NM: userName,
        TEL_NO: telNo,
        AUTH_NO: authNo,
      },
    };
    const messageOptions = {
      showConfirm: false,
      showAlert: true,
      alertTitle: "로그인 실패",
    };

    const response = await request(
      "/essk/user/login",
      requestOptions,
      messageOptions,
    );

    if (response) {
      const result = getValues(response);

      const showTopMenuYn = findShowTopMenuCode(result.commonCodes);
      const helpMenuYn = findHelpMenuYn(result.commonCodes);

      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
        result.user.accessToken,
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.COMMON_CODES,
        JSON.stringify(toHierarchy(result.commonCodes, "codeId", "parentId")),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.GRID_PSN,
        JSON.stringify(result.gridPsn),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.SHEET_STYLE,
        JSON.stringify(result.sheetStyle),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.AUTH_LISTS,
        JSON.stringify(result.authLists),
      );

      setUserAndMenuState({
        user: result.user,
        menu: result.menu,
        authBtn: result.authBtn,
        pageRenderType:
          response?.PAGE_RENDER_TYPE?.toUpperCase() || PAGE_RENDER_TYPE.PAGE,
        showTopMenuYn: showTopMenuYn,
        hierarchyMenu: toHierarchy(result.menu, "menuId", "parentId"),
        favoriteMenu: result.favoriteMenu,
        helpMenuYn: helpMenuYn,
        authLists: result.authLists,
      });
    }
  };

  const checkAutoLoginByIp = async () => {
    const requestOptions = {};

    const response = await request("/check-auto-login-by-ip", requestOptions);
    if (response) {
      const result = getValues(response);

      const showTopMenuYn = findShowTopMenuCode(result.commonCodes);
      const helpMenuYn = findHelpMenuYn(result.commonCodes);

      setSessionStorageItem(
        LOCAL_STORAGE_KEYS.ACCESS_TOKEN,
        result.user.accessToken,
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.COMMON_CODES,
        JSON.stringify(toHierarchy(result.commonCodes, "codeId", "parentId")),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.GRID_PSN,
        JSON.stringify(result.gridPsn),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.SHEET_STYLE,
        JSON.stringify(result.sheetStyle),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.AUTH_LISTS,
        JSON.stringify(result.authLists),
      );

      setUserAndMenuState({
        user: result.user,
        menu: result.menu,
        authBtn: result.authBtn,
        pageRenderType:
          response?.PAGE_RENDER_TYPE?.toUpperCase() || PAGE_RENDER_TYPE.PAGE,
        showTopMenuYn: showTopMenuYn,
        hierarchyMenu: toHierarchy(result.menu, "menuId", "parentId"),
        favoriteMenu: result.favoriteMenu,
        helpMenuYn: helpMenuYn,
        authLists: result.authLists,
      });

      // navigate(import.meta.env.VITE_PUBLIC_URL + '/DigitalSignage/auto', { replace: true });
      window.location.href =
        import.meta.env.VITE_PUBLIC_URL + `/DigitalSignage/auto`;
    }
  };

  const refresh = async (pathname) => {
    const messageOptions = {
      showConfirm: false,
      showAlert: false,
      showLoad: false,
    };

    const response = await request("/user/authentication", {}, messageOptions);

    if (response) {
      const result = getValues(response);

      const { selectedTopMenu, selectedMenu } = findSelectedMenu(
        result.menu,
        pathname,
      );

      const showTopMenuYn = findShowTopMenuCode(result.commonCodes);
      const helpMenuYn = findHelpMenuYn(result.commonCodes);

      // setLocalStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, response.user.accessToken);
      // setLocalStorageItem(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, response.user.refreshToken);
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.COMMON_CODES,
        JSON.stringify(toHierarchy(result.commonCodes, "codeId", "parentId")),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.GRID_PSN,
        JSON.stringify(result.gridPsn),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.SHEET_STYLE,
        JSON.stringify(result.sheetStyle),
      );
      setLocalStorageItem(
        LOCAL_STORAGE_KEYS.AUTH_LISTS,
        JSON.stringify(result.authLists),
      );

      setUserAndMenuState({
        user: result.user,
        menu: result.menu,
        authBtn: result.authBtn,
        selectedTopMenu,
        selectedMenu,
        pageRenderType:
          response?.PAGE_RENDER_TYPE?.toUpperCase() || PAGE_RENDER_TYPE.PAGE,
        showTopMenuYn: showTopMenuYn,
        hierarchyMenu: toHierarchy(result.menu, "menuId", "parentId"),
        favoriteMenu: result.favoriteMenu,
        helpMenuYn: helpMenuYn,
        authList: result.authLists,
      });
    }
  };

  //Param : 사용자 아이디
  const logout = async (userId) => {
    const requestOptions = {
      method: "POST",
      body: {
        USER_ID: userId,
      },
    };
    const messageOptions = {
      showConfirm: false,
      showAlert : false,
      showLoad: false,
      showSuccess : false,
    };

    const response = await request("/logout", requestOptions, messageOptions);

    if (response) {
    }

    // TODO: 서버에서도 Logout 처리
    removeLocalStorageItem(LOCAL_STORAGE_KEYS.AUTH_LISTS);
    removeSessionStorageItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    removeSessionStorageItem('password-confirmed');
    removeLocalStorageItem(LOCAL_STORAGE_KEYS.COMMON_CODES);
    resetUserAndMenuState();

    if (getLocalStorageItem(LOCAL_STORAGE_KEYS.AZURE_LOGIN) == "Y") {
      removeLocalStorageItem(LOCAL_STORAGE_KEYS.AZURE_LOGIN);
      const response = await request("/azure/logout");
      if (response.url) {
        window.location.href = response.url;
      }
    } else {
      navigate(import.meta.env.VITE_PUBLIC_URL + "/login", { replace: true });
    }

    localStorage.clear();
  };

  const findShowTopMenuCode = (codes) => {
    const code = codes.find((c) => c.codeId === "SYS_OPT_SHOW_TOP_MENU");
    if (code) {
      return code.propStr1;
    }
    return null;
  };

  const findHelpMenuYn = (codes) => {
    const code = codes.find((c) => c.codeId === "SYS_SHOW_HELP_MENU");
    if (code) {
      return code.propStr1;
    }
    return null;
  };

  const getValues = (response) => {
    let rtnJson = {};

    if (typeof response.USER === "string") {
      response.USER = JSON.parse(response.USER);
    }
    rtnJson.user = {
      plantCd: response.USER?.PLANT_CD,
      compCd: response.USER?.COMP_CD,
      userName: response.USER?.USER_NM,
      userId: response.USER?.USER_ID,
      userTelNo: response.USER?.USER_TEL,
      empNo: response.USER?.EMP_NO,
      userEmail: response.USER?.USER_EMAIL,
      imagePath: response.USER?.IMAGE_PATH,
      deptCd: response.USER?.DEPT_CD,
      deptName: response.USER?.DEPT_NAME,
      positionId: response.USER?.POSITION_CD,
      positionName: response.USER?.POSITION_NAME,
      isTouchScreenDevice: response.USER?.IS_TOUCH_SCREEN_DEVICE,
      accessToken: response.USER?.ACCESS_TOKEN,
      propStr1: response.USER?.PROP_STR1,
      temp02: response.USER?.PROP_STR2,
      temp03: response.USER?.PROP_STR3,
      temp04: response.USER?.PROP_STR4,
      temp05: response.USER?.PROP_STR5,
      loginSuccessDT: response.USER?.LOGIN_SUCCESS_DT,
      ipAddress: response.USER?.IP_ADDRESS,
      expiredCheck: response.USER?.EXPIRED_CHECK,
    };

    if (typeof response.MENU === "string") {
      response.MENU = JSON.parse(response.MENU);
    }
    rtnJson.menu = response.MENU?.map((menu) => ({
      menuId: menu?.MENU_ID,
      menuName: menu?.MENU_NM,
      helpYn: menu?.HELP_YN,
      level: menu?.level,
      parentId: menu?.PARENT_ID,
      programPath: menu?.PGM_PATH,
      programId: menu?.PGM_ID,
      sideYn: menu?.SIDE_YN,
      displayYn: menu?.DISPLAY_YN,
      programRoutePath: menu?.ROUTING_PATH,
      menuNameList: menu?.MENU_NAME_LIST,
    }));

    if (typeof response.AUTH_BTN === "string") {
      response.AUTH_BTN = JSON.parse(response.AUTH_BTN);
    }
    rtnJson.authBtn = response.AUTH_BTN?.map((btn) => ({
      menuId: btn?.MENU_ID,
      btnId: btn?.BTN_ID,
      btnType: btn?.BTN_TYPE,
    }));

    if (typeof response.FAVORITE_MENU === "string") {
      response.FAVORITE_MENU = JSON.parse(response.FAVORITE_MENU);
    }
    rtnJson.favoriteMenu = response.FAVORITE_MENU?.map((menu) => ({
      menuId: menu.MENU_ID,
      isActive: menu.IS_ACTIVE,
      menuName: menu.MENU_NM,
      programPath: menu.PGM_PATH,
      programRoutePath: menu.ROUTING_PATH,
    }));

    if (typeof response.COMMON_CODES === "string") {
      response.COMMON_CODES = JSON.parse(response.COMMON_CODES);
    }
    rtnJson.commonCodes = response.COMMON_CODES?.map((code) => ({
      codeId: code.CODE_ID,
      codeName: code.CODE_NM,
      parentId: code.PARENT_ID,
      codeDomainCd: code.CODE_DOMAIN_CD,
      note: code.CODE_DESC,
      ord: code.ORD,
      propStr1: code.PROP_STR1,
      temp02: code.PROP_STR2,
      temp03: code.PROP_STR3,
      temp04: code.PROP_STR4,
      temp05: code.PROP_STR5,
      useYn: code.USE_YN,
      regId: code.REG_ID,
      regDate: code.REG_DT,
      modId: code.MOD_ID,
      modDate: code.MOD_DT,
    }));

    if (typeof response.GRID_PSN === "string") {
      response.GRID_PSN = JSON.parse(response.GRID_PSN);
    }
    rtnJson.gridPsn = response.GRID_PSN?.map((item) => ({
      menuId: item.MENU_ID,
      gridId: item.GRID_ID,
      columnInfo: item.COLUMN_INFO,
      frozenColumns: item.FROZEN_COL_CNT,
    }));

    if (typeof response.SHEET_STYLE === "string") {
      response.SHEET_STYLE = JSON.parse(response.SHEET_STYLE);
    }
    rtnJson.sheetStyle = response.SHEET_STYLE?.map((item) => ({
      MENU_ID: item.MENU_ID,
      GRID_ID: item.GRID_ID,
      SHEET_IDX: item.SHEET_IDX,
      SHEET_NM: item.SHEET_NM,
      CELL_STYLE: item.CELL_STYLE,
      MERGE_RANGE: item.MERGE_RANGE,
      READ_ONLY: item.READ_ONLY,
      FIX_AREA: item.FIX_AREA,
      CUSTOM_MERGE_YN: item.CUSTOM_MERGE_YN,
      SHEET_UUID: item.SHEET_UUID,
      FROZEN_CELL: item.FROZEN_CELL,
    }));

    if (typeof response.AUTH_LISTS === "string") {
      response.AUTH_LISTS = JSON.parse(response.AUTH_LISTS);
    }
    rtnJson.authLists = response.AUTH_LISTS?.map((item) => ({
      authorityId: item?.AUTH_ID,
      userId: item?.USER_ID,
    }));

    return rtnJson;
  };

  return {
    login,
    logout,
    refresh,
    esskNonUserLogin,
    azureLogin,
    azureSecure,
    checkAutoLoginByIp,
  };
}
